import _ from 'lodash';
import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useLocal } from './../environment';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import ObjectID from 'bson-objectid';
import { arrayAny } from '../../utils/arrayUtils';
import { msalConfig } from '../../app';

class KanbanAPI {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiKanBan.scopes.write
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.baseUrl = useLocal ?
            "http://localhost:8087/api/kanban" :
            "https://apim.ianrobertson.co.uk/api/kanban/v1";

        this.accessTokenService = new AccessTokenHelper();
    }

    static boards = [];
    static templateData = { items: [] };

    #getBoardFromCache = id => {

        const board = KanbanAPI.boards.find(b => b.id === id);
        return board;
    };

    #addBoardToCache = board => {

        const existingBoard = this.#getBoardFromCache(board.id);
        if (!existingBoard) {
            KanbanAPI.boards.push(board);
        }
        else {
            // replace the whole property...
            const existingBoardIndex = _.findIndex(KanbanAPI.boards, b => b.id === board.id);
            KanbanAPI.boards.splice(existingBoardIndex, 1); // Remove current version
            KanbanAPI.boards.splice(existingBoardIndex, 0, board); // Insert new version in its place
        }
    };

    #addBoardsToCache = boards => {

        for (const board of boards) {
            this.#addBoardToCache(board);
        }
    };

    #getTileFromCache = tileId => {
        for (const board of KanbanAPI.boards) {
            for (const column of board.columns || []) {
                const tile = column.items?.find(item => item.id === tileId);
                if (tile) {
                    return tile;
                }
            }
        }
        return null;
    };

    addProject = (projectName, companyId = null, propertyId = null) => polly()
        .logger(error => {
            console.error("An error occurred in addProject", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(async accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);

                        const options = {
                            method: "POST",
                            headers: headers
                        };

                        try {
                            const response = await fetch(`${this.baseUrl}/create/${projectName}/${companyId}/${propertyId}`, options);
                            const data = await response.json();
                            const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                            this.#addBoardToCache(result);
                            return resolve(result);
                        } catch (error) {
                            console.log(error);
                            return reject(error);
                        }
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getProjectData = () => polly()
        .logger(error => {
            console.error("An error occurred in getProjectData", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                // Check if we have cached boards and templates
                if (KanbanAPI.boards.length > 0 && KanbanAPI.templateData.items && KanbanAPI.templateData.items.length > 0) {

                    return resolve(KanbanAPI.boards);
                }

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        fetch(`${this.baseUrl}/GetMyBoards`, options)
                            .then(response => response.json())
                            .then(data => {
                                const boards = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                this.#addBoardsToCache(boards);

                                this.loadSystemTemplates()
                                    .then(() => {
                                        return resolve(boards);
                                    });
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getProjectsByProperty = (companyId, propertyId, taskType = null) => polly()
        .logger(error => {
            console.error("An error occurred in getProjectsByProperty", error);
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        const url = taskType ? `${this.baseUrl}/boards/${companyId}/${propertyId}/${taskType}` : `${this.baseUrl}/boards/${companyId}/${propertyId}`;
                        fetch(url, options)
                            .then(response => response.json())
                            .then(data => {
                                const boards = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                this.#addBoardsToCache(boards);
                                return resolve(boards);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getProject = projectId => polly()
        .logger(error => {
            console.error("An error occurred in getProject", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                // Check cache first
                const cachedBoard = this.#getBoardFromCache(projectId);
                if (cachedBoard) {

                    return resolve(cachedBoard);
                }

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        fetch(`${this.baseUrl}/board/${projectId}`, options)
                            .then(response => {
                                if (response.status === 404) {
                                    return resolve(null);
                                }
                                return response.json();
                            })
                            .then(data => {
                                if (data) {
                                    const board = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                    this.#addBoardToCache(board);
                                    return resolve(board);
                                }
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    listenForUpdates = () => {
        //TODO subscribe to SignalR updates...
    };

    upsertTile = (tile, taskType = "All") => polly()
        .logger(error => {
            console.error("An error occurred in upsertTile", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "POST",
                            headers: headers,
                            body: JSON.stringify(cleanUpJsonForTransport(tile))
                        };

                        const url = `${this.baseUrl}/UpsertTask?taskType=${taskType}`
                        fetch(url, options)
                            .then(response => response.json())
                            .then(data => {
                                const board = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                this.#addBoardToCache(board);
                                return resolve(board);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    moveTile = async movedItem => {
        const board = await this.upsertTile(movedItem);
        return board;
    };

    loadSystemTemplates = () => polly()
        .logger(error => {
            console.error("An error occurred in loadSystemTemplates", error);
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {
                
                // Check if templates are already cached
                if (KanbanAPI.templateData.items && KanbanAPI.templateData.items.length > 0) {

                    return resolve(KanbanAPI.templateData);
                }
                
                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(async accessTokenResponse => {
                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;
                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        try {
                            const response = await fetch(`${this.baseUrl}/GetSystemTemplates`, options);
                            const templateData = await response.json();
                            KanbanAPI.templateData = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(templateData)));
                            return resolve(KanbanAPI.templateData);
                        } catch (error) {
                            console.log(error);
                            return reject(error);
                        }
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getTemplateData = () => KanbanAPI.templateData;

    addNewTask = async (board, name, description, createdBy, tileTemplate, tasks) => {

        const thisBoard = _.find(KanbanAPI.boards, b => b.Id === board.Id);
        const firstColumn = _.minBy(board.columns, c => c.displayOrder);

        for (const t of tasks) {
            t.isCompleted = false;
        }

        let count = 0;

        thisBoard.columns.forEach(col => {
            count += col.items.length;
        });

        const newTile = {
            id: new ObjectID().toHexString(),
            name: name,
            description: description,
            order: count,
            boardId: board.id,
            columnId: firstColumn.columnId,
            templateId: tileTemplate.id,
            subTasks: tileTemplate && arrayAny(tileTemplate.subTasks) ? _.orderBy(tileTemplate.subTasks.find(st => st.displayOrder === 0).items, st => st.order, ['asc']) : [],
            tags: []
        };

        if (tileTemplate && tileTemplate.isInspection) {
            newTile.taskType = "Inspection"; // TODO: maybe the template should have a taskType?
        }

        firstColumn.items = [...firstColumn.items, newTile];

        board = await this.upsertTile(newTile);
        return board;
    };

    addSubTask = async (project, tileId, newTask) => {

        const thisProject = _.find(KanbanAPI.boards, b => b.Id === project.Id);
        const tile = _.find(_.flatMap(thisProject.columns, t => t.items), t => t.id === tileId);

        newTask.subTaskId = new ObjectID().toHexString();
        newTask.editOn = true;
        newTask.order = tile.subTasks.length;
        newTask.autoComplete = false;

        tile.subTasks.push(newTask);

        const board = await this.upsertTile(tile);
        return board;

    };

    deleteSubTask = async (project, tileId, subTask) => {

        const thisProject = _.find(KanbanAPI.boards, b => b.Id === project.Id);

        for (const col of thisProject.columns) {
            for (const tile of col.items) {
                if (tile.id === tileId) {
                    for (const st of tile.subTasks) {
                        if (subTask.subTaskId === st.subTaskId) {
                            const index = tile.subTasks.findIndex(t => t.subTaskId === st.subTaskId);
                            tile.subTasks.splice(index, 1); // Remove current version
                            const board = await this.upsertTile(tile);
                            return board;
                        }
                    }
                }

            }
        }


    };

    updateSubTask = async (project, tileId, updatedTask) => {

        const thisProject = _.find(KanbanAPI.boards, b => b.Id === project.Id);
        const tile = _.find(_.flatMap(thisProject.columns, t => t.items), t => t.id === tileId);

        const originalTaskIndex = tile.subTasks.findIndex(st => st.subTaskId === updatedTask.subTaskId);

        tile.subTasks.splice(originalTaskIndex, 1, updatedTask);

        const board = await this.upsertTile(tile);
        return board;

    };

    // toggleSubTaskComplete = async (project, tileId, subTaskId) => {

    //     const thisProject = KanbanAPI.boards.find(b => b.Id === project.Id);
    //     const tile = _.flatMap(thisProject.columns, t => t.items).find(t => t.id === tileId);

    //     const originalTaskIndex = tile.subTasks.findIndex(st => st.subTaskId === subTaskId);
    //     const subTask = tile.subTasks.find(st => st.subTaskId === subTaskId);

    //     subTask.isCompleted = !subTask.isCompleted;

    //     tile.subTasks.splice(originalTaskIndex, 1, subTask);

    //     const board = await this.upsertTile(tile);
    //     return board;
    // };

    // setSubTaskCheckCompleteStatus = async (project, tileId, subTaskId, status) => {

    //     const thisProject = KanbanAPI.boards.find(b => b.id === project.id);
    //     const tile = _.flatMap(thisProject.columns, t => t.items).find(t => t.id === tileId);

    //     const originalTaskIndex = tile.subTasks.findIndex(st => st.subTaskId === subTaskId);
    //     const subTask = tile.subTasks.find(st => st.subTaskId === subTaskId);

    //     subTask.checkCompleteStatus = status;

    //     tile.subTasks.splice(originalTaskIndex, 1, subTask);

    //     const board = await this.upsertTile(tile);
    //     return board;
    // };

    updateBoard = board => polly()
        .logger(error => {
            console.error("An error occurred in updateBoard", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "PATCH",
                            headers: headers,
                            body: JSON.stringify(cleanUpJsonForTransport(board))
                        };

                        fetch(`${this.baseUrl}/update/${board.id}`, options)
                            .then(response => response.json())
                            .then(data => {
                                const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                this.#addBoardToCache(result);
                                return resolve(result);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    moveColumn = async (board, column, direction) => {

        const thisBoard = KanbanAPI.boards.find(b => b.id === board.id);
        const thisColumn = thisBoard.columns.find(c => c.columnId === column.columnId);

        let positionToSwitchWith = -1;
        let columnToSwitchWith = null;

        switch (direction) {
            case "left":
                positionToSwitchWith = thisColumn.displayOrder - 1;
                columnToSwitchWith = thisBoard.columns.find(c => c.displayOrder === positionToSwitchWith);

                columnToSwitchWith.displayOrder += 1;
                thisColumn.displayOrder -= 1;
                break;
            case "right":
            default:
                positionToSwitchWith = thisColumn.displayOrder + 1;
                columnToSwitchWith = thisBoard.columns.find(c => c.displayOrder === positionToSwitchWith);

                columnToSwitchWith.displayOrder -= 1;
                thisColumn.displayOrder += 1;
        }

        thisBoard.columns = _.orderBy(thisBoard.columns, c => c.displayOrder, ['asc']);
        thisBoard.columns.forEach((col, i) => {
            col.displayOrder = i;
        });

        const result = await this.updateBoard(thisBoard);
        result.columns = _.orderBy(result.columns, c => c.displayOrder, ['asc']);

        result.columns.forEach((col, i) => {
            col.displayOrder = i;
        });

        return result;
    };

    addColumnToProject = async (board, newColumnName) => {

        const thisBoard = KanbanAPI.boards.find(b => b.id === board.id);

        const newColumn = {
            columnId: new ObjectID().toHexString(),
            displayOrder: thisBoard.columns.length,
            name: newColumnName,
            items: []
        };

        thisBoard.columns.push(newColumn);

        const result = await this.updateBoard(thisBoard);
        return result;
    };

    deleteColumn = async (board, column) => {

        const thisBoard = KanbanAPI.boards.find(b => b.id === board.id);
        const columnToDelete = thisBoard.columns.find(c => c.columnId === column.columnId);
        const previousColumn = thisBoard.columns.find(c => c.displayOrder === Math.max(0, columnToDelete.displayOrder - 1));

        columnToDelete.items.forEach(async tile => {
            tile.columnId = previousColumn.columnId;
            await this.upsertTile(tile);
            previousColumn.items.push(tile);
        });

        thisBoard.columns = thisBoard.columns.filter(c => c.columnId !== columnToDelete.columnId);

        thisBoard.columns.forEach((col, i) => {
            col.displayOrder = i;
        });

        const result = await this.updateBoard(thisBoard);
        return result;
    };

    deleteTiles = tiles => polly()
        .logger(error => {
            console.error("An error occurred in deleteTiles", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "DELETE",
                            headers: headers,
                            body: JSON.stringify(cleanUpJsonForTransport(tiles))
                        };

                        fetch(`${this.baseUrl}/DeleteTasks`, options)
                            .then(response => response.json())
                            .then(data => {
                                const board = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                this.#addBoardToCache(board);
                                return resolve(board);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });


    getSingleTileById = tileId => polly()
        .logger(error => {
            console.error("An error occurred in getSingleTileById", error); // will be hit 5 times if issues
        })
        .waitAndRetry(5)
        .executeForPromise(() => {
            return new Promise((resolve, reject) => {

                // Check cache first for the tile
                const cachedTile = this.#getTileFromCache(tileId);
                if (cachedTile) {

                    return resolve(cachedTile);
                }

                if (this.inProgress !== InteractionStatus.None) {
                    return reject("Login interaction in-progress");
                }

                this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                    .then(accessTokenResponse => {

                        if (!!!accessTokenResponse) {
                            return reject("No access token");
                        }

                        const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                        const headers = new Headers();
                        headers.append("Authorization", bearer);
                        headers.append("Content-Type", "application/json");

                        const options = {
                            method: "GET",
                            headers: headers
                        };

                        fetch(`${this.baseUrl}/task/${tileId}`, options)
                            .then(response => response.json())
                            .then(data => {
                                const task = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                return resolve(task);
                            })
                            .catch(error => {
                                console.log(error);
                                return reject(error);
                            });
                    })
                    .catch((error) => {
                        if (error instanceof InteractionRequiredAuthError) {
                            // Let centralized auth handling manage redirects
                        }
                        console.log(error);
                        return reject(error);
                    });
            });
        });

    getMaintenanceCount = () => {
        return new Promise((resolve) => {
            // Temporary: return "10" after 5s delay until backend is ready
            setTimeout(() => {
                resolve("10");
            }, 5000);
        });
    }

    findSubTasks = async (boardId, pageNumber, itemsPerPage, filterPredicate, sortPredicate, sortDirection) => {
        const board = KanbanAPI.boards.find(b => b.id === boardId) ?? await this.getProject(boardId);
        if (!!board && board.columns && board.columns.length > 0) {

            const finalColumn = _.maxBy(board.columns, c => c.displayOrder);
            const finalColumnItems = finalColumn?.items || [];
            const allSubTasks = _.orderBy(finalColumnItems.filter(tile => filterPredicate(tile)), tile => sortPredicate(tile), sortDirection);

            const totalCount = allSubTasks.length;

            // Example pageNumber = 0, itemsPerPage = 3, I expect items [0], [1], [2]
            // Example pageNumber = 1, itemsPerPage = 3, I expect items [3], [4], [5]
            // Example pageNumber = 2, itemsPerPage = 3, I expect items [6], [7], [8]

            const maxIndex = ((pageNumber + 1) * itemsPerPage);
            const minIndex = maxIndex - itemsPerPage;

            const pagedTasks = allSubTasks.slice(minIndex, maxIndex);

            const paginationResult = {
                pagedTasks: pagedTasks,
                totalCount: totalCount,
                itemsOnPage: pagedTasks.length
            }

            return paginationResult;
        }

        return null;
    };
}

export default KanbanAPI;